/**
 * Cartão de partida — home, lista do grupo e histórico.
 *
 * A partida de hoje ou amanhã ganha destaque visual, porque é a única
 * informação que importa naquele momento.
 */

import type { GameSummary } from '@nossorachao/types';
import { relativeDayLabel, weekdayLabel } from '@nossorachao/utils';
import { Clock, MapPin } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { Badge, Card, ProgressBar } from '@/components/ui';

const ATTENDANCE: Record<string, { label: string; tone: 'ok' | 'bad' | 'warn' | 'neutral' | 'info' }> =
  {
    CONFIRMED: { label: 'Confirmado', tone: 'ok' },
    DECLINED: { label: 'Fora', tone: 'bad' },
    MAYBE: { label: 'Talvez', tone: 'warn' },
    WAITLIST: { label: 'Lista de espera', tone: 'info' },
    NO_RESPONSE: { label: 'Aguardando', tone: 'neutral' },
  };

export function GameCard({
  game,
  showGroup = false,
}: {
  game: GameSummary;
  showGroup?: boolean;
}) {
  const navigate = useNavigate();

  const scheduledAt = new Date(game.scheduledAt);
  const relative = relativeDayLabel(scheduledAt);
  const imminent = relative === 'hoje' || relative === 'amanhã';
  const finished = game.status === 'FINISHED';
  const canceled = game.status === 'CANCELED';

  const time = new Intl.DateTimeFormat('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(scheduledAt);

  const attendance = game.myAttendance ? ATTENDANCE[game.myAttendance] : null;
  const spots = game.maxPlayers - game.confirmedCount;

  return (
    <Card
      onClick={() => navigate(`/partidas/${game.id}`)}
      accent={
        canceled
          ? 'rgb(217 45 32)'
          : imminent && !finished
            ? 'rgb(18 183 106)'
            : undefined
      }
    >
      <div className="space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            {showGroup ? (
              <p className="text-[11px] font-bold uppercase tracking-wide text-ink-3">
                {game.groupName}
              </p>
            ) : null}

            <p className="text-base font-bold">
              {imminent && !finished
                ? relative.charAt(0).toUpperCase() + relative.slice(1)
                : weekdayLabel(scheduledAt.getDay())}
              <span className="ml-2 font-semibold text-ink-2">{time}</span>
            </p>

            {game.venueName ? (
              <p className="mt-0.5 flex items-center gap-1 truncate text-[13px] text-ink-3">
                <MapPin className="h-3.5 w-3.5 shrink-0" aria-hidden />
                {game.venueName}
              </p>
            ) : null}
          </div>

          {canceled ? (
            <Badge tone="bad">Cancelada</Badge>
          ) : finished ? (
            <Badge tone="neutral">Encerrada</Badge>
          ) : attendance ? (
            <Badge tone={attendance.tone}>{attendance.label}</Badge>
          ) : null}
        </div>

        {!finished && !canceled ? (
          <div className="space-y-1.5">
            <div className="flex items-center justify-between text-[13px]">
              <span className="tabular font-semibold text-ink-2">
                {game.confirmedCount} / {game.maxPlayers} confirmados
              </span>
              <span
                className={
                  spots === 0 ? 'text-bad' : spots <= 3 ? 'text-warn' : 'text-ink-3'
                }
              >
                {spots === 0 ? 'Lotada' : `${spots} vaga${spots === 1 ? '' : 's'}`}
              </span>
            </div>

            <ProgressBar
              value={game.confirmedCount}
              max={game.maxPlayers}
              tone={spots === 0 ? 'ok' : spots <= 3 ? 'warn' : 'brand'}
            />
          </div>
        ) : finished ? (
          <p className="flex items-center gap-1 text-[13px] text-ink-3">
            <Clock className="h-3.5 w-3.5" aria-hidden />
            {new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: 'long' }).format(
              scheduledAt,
            )}
          </p>
        ) : null}
      </div>
    </Card>
  );
}
